import { isValidCoordinates } from '@ma-js-common/map-helpers';
import turfBBox from '@turf/bbox';

import {
    FIT_FEATURES_MAX_ZOOM,
    OTHER_PAST_SALE_FEATURE_ID,
    REALTOR_FEATURE_ID,
    REFERENCE_FEATURE_ID,
    RELEVANT_PAST_SALE_FEATURE_ID,
    TRANSIT_STATION_FEATURE_ID,
} from '../constants.es6';
import { hasTouchEvents, isMobile } from '../device';
import { addClass, removeClass, toggleClass } from '../domHelpers.es6';
import Ga from '../ga';
import MAEvent from '../ma-event';
import { setMediumParam } from '../medium';
import HTTPService from '../services/HTTPService.es6';
import { deepEqual, isFunction, isIE } from '../utils';
import Wa from '../wa';

import * as MapHelpers from './helpers.es6';
import InfoWindowManager from './infowindow-manager.es6';
import MapboxGL from './mapbox-gl-loader.es6';

/**
 * Max marker count by layer form mobile
 * @type {Number}
 */
const DATA_SOURCE_CACHE_SIZE_MOBILE = 100;

/**
 * Max marker count by layer for desktop
 * @type {Number}
 */
const DATA_SOURCE_CACHE_SIZE_DESKTOP = 300;

/**
 * Padding size when using `fitBounds`
 * @type {number}
 */
export const FIT_BOUNDS_PADDING = {
    top: 30,
    bottom: 50,
    left: 30,
    right: 30,
};

/**
 * Mapbox style
 * TODO find a better place for this
 * @type {string}
 */
const MAPBOX_STYLE = 'mapbox://styles/meilleursagents/cjfm7js7u0o552snxqa6g7vxr';

/**
 * Save browser is IE
 * @type {Boolean}
 */
const IS_IE = isIE();

/**
 * Class name for loading state
 * @type {Dtring}
 */
const LOADING_WRAPPER_ACTIVE = 'loading-wrapper--loading';

/**
 * Feature marker size
 * @type {Number}
 */
export const MARKER_SIZE = 16;

/**
 * Usage :
 *
 * ```
     const wrapperElement = document.querySelector('[data-vector-map]');
     if (!wrapperElement) {
        return;
     }

     const realtorId = wrapperElement.getAttribute('data-realtor-id')

     const realtorMap = new MapboxMap(wrapperElement)
     const featuresURLParams = {
            realtor_id: realtorId,
            realtor_products: wrapperElement.getAttribute('data-realtor-products'),
        }
     const realtorCoordinates = {
            lat: Number(wrapperElement.getAttribute('data-realtor-coordinates-lat')),
            lng: Number(wrapperElement.getAttribute('data-realtor-coordinates-lng')),
        }
     const clickHandler = (event, feature) => {
            loadPastSalePopin(feature.properties.url, 'past_sale_marker')
        }
     realtorMap.registerExternalMarker('loaded-realtor-marker', '/static/images/realtor-marker@2x.png', realtorCoordinates)
     realtorMap.registerFeatures(
         REFERENCE_FEATURE_ID,
         '/carte/biens-vendus',
         featuresURLParams,
        {
            updateOnBoundsChanged = false,
            fitClosestFeaturesAtPoint = undefined,
            clickHandler = () => {},
            iconColor = 'blue',
            infoWindowParameters = {},
            geoJSONKey = null,
        },
     )
 * ```
 *
 * DOM usage :
 *
 * ```
     <div class="vector-map loading-wrapper" style="height:200px"
         data-vector-map
         data-ua-event-category="Page agence"
         data-ua-event-label-prefix="from:realtor:map"
         data-wa-source="realtor:map:past_sales"
         data-wa-name-prefix="realtor:map"
     >
         <div class="loading-container container--no-horizontal-gutter container--no-vertical-gutter">
             <div class="spinner--dots">
                <div class="bounce1"></div> <div class="bounce2"></div> <div class="bounce3"></div>
             </div>
         </div>
         <div class="vector-map__wrapper">
            <div id="mapbox-map" class="vector-map__map"></div>
         </div>
         <div class="vector-map__infowindow" data-map-infowindow>{# Filled by JS #}</div>
         <div class="no-webgl-warning hide" data-map-nowebgl-warning>
             <p class="no-webgl-warning__title">Notre carte des prix ne peut pas s’afficher sur votre navigateur !</p>
             <p class="no-webgl-warning__text">Pour voir cette carte, n’hésitez pas à télécharger un navigateur plus récent.</p>
         </div>
     </div>
 * ```
 *
 */
export default class MapboxMap {
    /**
     * Wrapper for this.wrapperDataAttribute
     * @type {Element}
     */
    wrapperElement = null;

    /**
     * Container for containerId
     * @type {Element}
     */
    container = null;

    /**
     * Mapbox map instance
     * @type {MapboxGL.Map}
     */
    map = null;

    /**
     * Mapbox style is currently loading
     * @type {boolean}
     */
    styleIsLoading = false;

    /**
     * Contains all registered feature layers
     * @type {Array}
     * @private
     */
    _featureLayers = [];

    /**
     * Cache collection of all features
     * @type {Map}
     */
    dataSourceCache = new Map();

    /**
     * Handle infowindow opening and closing
     * @type {InfoWindowManager}
     */
    infoWindowManager = null;

    /**
     * Create instance of Mapbox Map, bind events and return it
     *
     * @return {MapboxMap}
     * @param {Element} wrapperElement Selector for findind map wrapper
     * @param {Element|String} containerElement Element ID or HTML Element for containing Mapbox map Canvas
     * @param {{showControls: Boolean, showFullScreencontrols: Boolean, disableInfoWindows: Boolean, bounds: Array, ...MapboxOptions}} options Options to pass to MapboxGL.Map constructor https://www.mapbox.com/mapbox-gl-js/api/#map
     */
    constructor(wrapperElement, containerElement = 'mapbox-map', options = {}) {
        if (!wrapperElement) {
            throw new Error(`Unable to find ${wrapperElement} element`);
        }
        this.wrapperElement = wrapperElement;

        this.container =
            typeof containerElement === 'string'
                ? document.getElementById(containerElement)
                : containerElement;
        if (!this.container) {
            throw new Error(`Unable to find #${this.containerId} element`);
        }

        if (!MapboxGL) {
            const nowebglWarningElement = document.querySelector('[data-map-nowebgl-warning]');
            if (!nowebglWarningElement) {
                throw new Error(`Unable to find [data-map-nowebgl-warning] element`);
            }
            removeClass(nowebglWarningElement, 'hide');
            return null;
        }

        addClass(this.wrapperElement, LOADING_WRAPPER_ACTIVE);

        const {
            showControls,
            showFullScreencontrols,
            disableInfoWindows,
            initialInfoWindowTemplate,
            ...mapOptions
        } = options;
        this.options = {
            container: this.container,
            zoom: 4,
            center: [1.999512, 46.800059], // France center by default
            // Mapbox v3 compatibility: preserve v1 pitch behavior
            maxPitch: 60, // v3 default is 85, we want v1 default of 60
            touchPitch: false, // disable pitch via touch gestures
            pitchWithRotate: false, // disable pitch when rotating
            ...mapOptions, // allow callers to override if needed
        };

        // Prefer use instance method `setStyle` rather than pass `style` in constructor
        // to defer map instructions and avoid `Style is not done loading` error
        const style = this.options.style || MAPBOX_STYLE;
        delete this.options.style;

        // Create instance
        this.map = new MapboxGL.Map(this.options);
        this.setStyle(style);

        // Instantiate infowindow manager
        if (!disableInfoWindows) {
            this.infoWindowManager = new InfoWindowManager(
                this.map,
                this.wrapperElement,
                initialInfoWindowTemplate,
            );
        }

        // Add zoom controls to the map.
        if (showControls) {
            this.map.addControl(new MapboxGL.NavigationControl());
        }
        if (showFullScreencontrols) {
            this.map.addControl(new MapboxGL.FullscreenControl());
        }
        // Disable map 3D rotation using right click + drag
        this.map.dragRotate.disable();
        // Disable map rotation using touch rotation gesture
        this.map.touchZoomRotate.disableRotation();
        // When map is loaded and ready to interact with
        this.map.on('load', this.onLoad.bind(this));
    }

    /**
     * Toggle loading state of the map
     */
    toggleLoadingState() {
        if (this.wrapperElement) {
            toggleClass(this.wrapperElement, LOADING_WRAPPER_ACTIVE);
        }
    }

    /**
     * Executed after styles and tiles loaded
     */
    onLoad() {
        new Promise(resolve => {
            if (typeof this.options.onLoad === 'function') {
                this.options.onLoad(this.map, this, resolve);
            } else {
                resolve();
            }
        }).then(() => {
            // Bind user interactions events
            if (hasTouchEvents()) {
                this.bindTouchEvents();
            } else {
                this.bindMouseEvents();
            }

            // Analytics initialisation
            this.setTracking();

            // All good
            removeClass(this.wrapperElement, LOADING_WRAPPER_ACTIVE);
        });
    }

    /**
     * Move layer to the top.
     * @param  {String} layerId Layer id
     */
    moveToTop(layerId) {
        this.map.moveLayer(layerId);
    }

    /**
     * Add an external marker to the map.
     * @param  {String} name        Marker name (layer id)
     * @param  {String} iconName    Icon name
     * @param  {String} iconUrl     Icon external URL
     * @param  {Object} coordinates Coordinates of marker
     * @returns {Promise} Promise which resolves when marker is registered
     */
    registerExternalMarker(name, iconUrl, coordinates) {
        return this.waitForStylesLoaded().then(
            () =>
                new Promise((resolve, reject) => {
                    this.map.loadImage(iconUrl, (error, image) => {
                        if (error) {
                            reject(new Error(`Error while loading image ${iconUrl}: ${error}`));
                        }

                        this.map.addImage(name, image);
                        this.registerMarker(name, name, coordinates, {
                            'icon-size': 0.4,
                        });
                        resolve();
                    });
                }),
        );
    }

    /**
     * Add a marker to the map as a layer.
     * @param  {String} name        Marker name (layer id)
     * @param  {String} iconName    Marker icon name
     * @param  {Object} iconOptions Layout option of icon
     * @param  {Object} coordinates Coordinates of marker
     * @return {Promise}
     */
    registerMarker(name, iconName, coordinates, iconOptions = {}) {
        return this.waitForStylesLoaded().then(() => {
            this.map.addLayer({
                id: name,
                type: 'symbol',
                source: {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: [
                            {
                                type: 'Feature',
                                geometry: {
                                    type: 'Point',
                                    coordinates: [coordinates.lng, coordinates.lat],
                                },
                            },
                        ],
                    },
                },
                layout: {
                    'icon-image': iconName,
                    'icon-allow-overlap': true,
                    ...iconOptions,
                },
            });

            return Promise.resolve();
        });
    }

    /**
     * Draw GeoJSON on map.
     * @param {String}   layerId Identifier of layer
     * @param {Object}   data    Layer data
     * @param {Object}   options options of the layer
     * @param {Function} onClick Add on click to the layer
     */
    registerGeoJSON(layerId, data, options = {}, onClick) {
        if (!layerId || !data) {
            return;
        }

        const { map } = this;
        const source = map.getSource(layerId);
        const { paint, before, ...layerOptions } = options;

        // Redraw GeoJSON if layer exists
        if (source) {
            source.setData(data);

            if (paint) {
                Object.keys(paint).forEach(property =>
                    map.setPaintProperty(layerId, property, paint[property]),
                );
            }
        } else {
            this.waitForStylesLoaded().then(() => {
                map.addSource(layerId, {
                    type: 'geojson',
                    data,
                });

                map.addLayer(
                    {
                        id: layerId,
                        type: 'fill',
                        source: layerId,
                        layout: {},
                        paint,
                        ...layerOptions,
                    },
                    before,
                );

                if (typeof onClick === 'function') {
                    map.on('click', layerId, onClick);

                    map.on('mouseenter', layerId, () => {
                        map.getCanvas().style.cursor = 'pointer';
                    });
                    map.on('mouseleave', layerId, () => {
                        map.getCanvas().style.cursor = '';
                    });
                }
            });
        }
    }

    /**
     * Remove GeoJSON from map.
     * @param {String} layerId  Identifier of layer
     */
    removeGeoJSON(layerId) {
        if (!layerId) {
            return;
        }

        const { map } = this;
        try {
            const layer = map.getLayer(layerId);
            if (layer) {
                map.removeLayer(layerId);
            }
        } catch (e) {
            global.console.warn(
                `Error while removing GeoJSON layer with id=${layerId}. It's sometimes due to an already removed map`,
                e,
            );
        }

        try {
            const source = map.getSource(layerId);
            if (source) {
                map.removeSource(layerId);
            }
        } catch (e) {
            global.console.warn(
                `Error while removing GeoJSON soruce with id=${layerId}. It's sometimes due to an already removed map`,
                e,
            );
        }
    }

    /**
     * Use this method to register features (colored dots) layer
     * Once map is ready the features will be draw
     * @param {String} layerId
     * @param {String} geoJSONUrlOrData Url of a JSON or a plain Object
     * @param {Object} geoJSONParams
     * @param {Boolean} updateOnBoundsChanged
     * @param {{lat: Number, lng: Number}} fitClosestFeaturesAtPoint
     * @param {Function} clickHandler
     * @param {String} iconColor
     * @param {String} hoverIconColor
     * @param {Array} infoWindowParameters Url params to call infowindow (e.g. `types` which allows to specify what kind of features the infowindow needs to fetch.
     * @param {Object} layerOptions Options to be pass to MapboxLayer (paint, ...)
     */
    registerFeatures(
        layerId,
        geoJSONUrlOrData,
        geoJSONParams,
        {
            updateOnBoundsChanged = false,
            fitClosestFeaturesAtPoint = undefined,
            clickHandler = () => {},
            iconColor = 'blue',
            hoverIconColor = iconColor,
            infoWindowParameters = {},
            withCache = true,
            onBoundsChangedCallback = null,
            geoJSONFetchCallback = null,
        } = {},
    ) {
        if (this._featureLayers.find(layer => layer.id === layerId)) {
            throw new Error(`Layer '${layerId}' already registered`);
        }

        if (fitClosestFeaturesAtPoint && !isValidCoordinates(fitClosestFeaturesAtPoint)) {
            throw new Error('`fitClosestFeaturesAtPoint` must be valid coordinates');
        }

        const layer = {
            id: layerId,
            geoJSONUrl: geoJSONUrlOrData,
            geoJSONParams,
            fitClosestFeaturesAtPoint,
            updateOnBoundsChanged,
            clickHandler,
            infoWindowParameters,
            withCache,
            onBoundsChangedCallback,
            geoJSONFetchCallback,
        };

        return this.waitForStylesLoaded().then(() => {
            this._featureLayers.push(layer);

            // Checking if we need to fetch or not
            if (typeof geoJSONUrlOrData !== 'string') {
                this.addSymbolLayer(layer.id, iconColor, {
                    geoJSONUrlOrData,
                    hoverIconColor,
                });
                return Promise.resolve();
            }

            this.addSymbolLayer(layer.id, iconColor, { hoverIconColor });

            // Reload features on bounds changes
            if (updateOnBoundsChanged) {
                this.onBoundsChanged(event => {
                    this.fetchFeatures(layer);
                    if (isFunction(layer.onBoundsChangedCallback)) {
                        layer.onBoundsChangedCallback(event);
                    }
                });
            }

            // Fetch once immediately
            return this.fetchFeatures(layer);
        });
    }

    /**
     * To update given layers options
     * @param {String} layerId layer id you want to patch
     * @param {Object} properties Properties of the layer you want to patch
     */
    updateFeaturesLayer(layerId, properties = {}) {
        const currentLayer = this._featureLayers.find(layer => layer.id === layerId);

        if (!currentLayer) {
            throw new Error('This layer does not exist');
        }

        Object.assign(currentLayer, properties);

        this.fetchFeatures(currentLayer);
    }

    /**
     * Fetch the JSON to get all features if the layer is visible
     * @param {String} layer
     */
    fetchFeatures(layer) {
        const params = {
            ...layer.geoJSONParams,
        };

        if (layer.updateOnBoundsChanged) {
            params.bounds = MapHelpers.getBoundsString(this.map.getBounds());
        }

        return HTTPService.get(layer.geoJSONUrl, params).then(response => {
            const geoJSON = isFunction(layer.geoJSONFetchCallback)
                ? layer.geoJSONFetchCallback(response.data)
                : response.data;

            return this.waitForStylesLoaded().then(() => this.drawFeatures(layer, geoJSON));
        });
    }

    /**
     * Draw a FeatureCollection on the map
     * @param {Object} layer
     * @param {FeatureCollection} data
     */
    drawFeatures(layer, data) {
        const layerId = layer.id;
        const withCache = !IS_IE && layer.withCache;
        const features = withCache
            ? this.computeFeaturesWithCache(layer, data.features)
            : data.features;
        const sourceData = this.map.getSource(layerId);

        sourceData.setData({ type: 'FeatureCollection', features });

        if (layer.fitClosestFeaturesAtPoint) {
            this.fitClosestFeatures(layer.fitClosestFeaturesAtPoint, features);
        }
    }

    /**
     * Compute features with a cache and capping system
     * Benefit: prevent feature disappearance on the map when moving on a short distance
     * Usage: Enabled by default, you can disable it by setting `withCache` option to false in the layer
     *
     * @param {String} layerId
     * @param {[]GeoJSON} features
     */
    computeFeaturesWithCache(layerId, features) {
        // No cache for IE, it does not implement Map correctly
        if (!(layerId in this.dataSourceCache)) {
            this.dataSourceCache[layerId] = new Map();
        }

        const cache = this.dataSourceCache[layerId];

        features.forEach(feature => {
            const key = `${layerId}-${feature.properties.id}`;
            if (cache.has(key)) {
                // Feature already present
                return;
            }
            cache.set(key, feature);
        });

        const cachedFeatures = Array.from(cache.values());

        // Keep last <DATA_SOURCE_CACHE_SIZE> items in array
        const cacheSize = isMobile()
            ? DATA_SOURCE_CACHE_SIZE_MOBILE
            : DATA_SOURCE_CACHE_SIZE_DESKTOP;

        if (cachedFeatures.length > cacheSize) {
            return cachedFeatures.splice(cachedFeatures.length - cacheSize, cacheSize);
        }

        return cachedFeatures;
    }

    /**
     * Recenter the map for seeing closest features from a specific point
     * @param {{lat: Number, lng: Number}} fromPoint
     * @param {Array<Object>} features
     */
    fitClosestFeatures(fromPoint, features) {
        // Do not fit if no features has been displayed
        if (features.length === 0) {
            return;
        }

        if (!isValidCoordinates(fromPoint)) {
            throw new Error('You must provide valid coordinates to `fitClosestFeatures`');
        }

        // Add a feature representing from point to be added in bounds
        const fromPointFeature = {
            properties: {
                coordinates: {
                    lng: fromPoint.lng,
                    lat: fromPoint.lat,
                },
            },
        };

        const closestFeatures = MapHelpers.extractClosestFeatures(features, fromPoint);
        closestFeatures.push(fromPointFeature);

        const bounds = MapHelpers.getBoundsForFeatures(closestFeatures);
        bounds.extend(new MapboxGL.LngLat(fromPoint.lng, fromPoint.lat));
        this.map.fitBounds(bounds, {
            padding: FIT_BOUNDS_PADDING,
            animate: false,
        });
    }

    /**
     * Recenter the map for seeing all features
     * @param {Object} Plain geoJSON
     */
    fitFeatures(geoJSON) {
        const bounds = turfBBox(geoJSON);
        this.map.fitBounds(bounds, {
            padding: FIT_BOUNDS_PADDING,
            animate: false,
        });
        if (this.map.getZoom() > FIT_FEATURES_MAX_ZOOM) {
            this.map.setZoom(FIT_FEATURES_MAX_ZOOM);
        }
    }

    /**
     * Reload all registered layers
     * @param {String} bounds
     */
    fetchAllFeatures(bounds) {
        this._featureLayers.forEach(layer => {
            this.fetchFeatures(layer.id, layer.url, { ...layer.urlParams, bounds });
        });
    }

    /**
     * Add a layer to handle features
     * @param {String} layerId Internal ID of the layer
     * @param {String} iconColor red|blue|purple
     * @param {Object} options extra options for symbol layer
     * @param {String} options.before Specify a layer id to place under the layer
     * @param {Object} options.geoJSONUrlOrData GeoJSON data
     * @param {String} options.hoverIconColor Color of icon when hovered over
     */
    addSymbolLayer(
        layerId,
        iconColor,
        { before = null, geoJSONUrlOrData = null, hoverIconColor = iconColor } = {},
    ) {
        this.map.addSource(layerId, {
            type: 'geojson',
            data: geoJSONUrlOrData || { type: 'FeatureCollection', features: [] },
        });

        const baseLayer = {
            id: layerId,
            type: 'symbol',
            source: layerId,
            layout: {
                'icon-image': `${iconColor}-dot`,
                'icon-allow-overlap': true, // https://www.mapbox.com/mapbox-gl-js/style-spec/#layout-symbol-icon-allow-overlap
            },
        };
        this.map.addLayer(baseLayer, before);

        const layerHover = {
            ...baseLayer,
            id: `${layerId}-hover`,
            layout: {
                'icon-image': `${hoverIconColor}-dot--hover`,
            },
            filter: ['==', 'id', ''],
        };
        this.map.addLayer(layerHover, before);
    }

    /**
     * Add a button to toggle the satellite style
     * It will be add in mapbox control container
     */
    setStyle(...args) {
        this.styleIsLoading = true;
        this.map.setStyle(...args);
        this.map.on('style.load', () => {
            this.styleIsLoading = false;
        });
    }

    /**
     * Some tasks cannot be done while style is loading
     * So this method can defer execution of a function if the style is loading
     * or immediately if not
     */
    waitForStylesLoaded() {
        return new Promise(resolve => {
            const listener = event => {
                if (event) {
                    this.map.off('style.load', listener);
                }
                resolve({});
            };
            if (this.styleIsLoading) {
                this.map.on('style.load', listener);
            } else {
                listener();
            }
        });
    }

    /**
     * Show info window
     * @param  {String} layerId Layer identifier
     * @param  {Object} feature Feature targeted
     */
    showInfoWindow(layerId, feature) {
        if (!this.infoWindowManager) {
            return;
        }
        this.map.setFilter(`${layerId}-hover`, ['==', 'id', feature.properties.id]);
        const params = this.getFeatureLayer(layerId).infoWindowParameters;
        this.infoWindowManager.loadMarkerInfoWindow(feature, params);
    }

    /**
     * Bind events for desktop
     * @listens {MapboxGL.MapMouseEvent} listen mouseenter, mousemove and click.
     * @listens {MouseEvent} listen click on [data-popin-button].
     */
    bindMouseEvents() {
        const layersIds = this._featureLayers.map(layer => layer.id);
        const layersOverIds = this._featureLayers.map(layer => `${layer.id}-hover`);
        const allLayersIds = layersOverIds.concat(layersIds);
        // Bind mouse event on features layers
        // Show an popup when hovering feature
        layersIds.forEach(layerId => {
            this.map.on('mouseenter', layerId, ({ features }) => {
                this.showInfoWindow(layerId, MapHelpers.getFirstPointFromFeatures(features));
            });
        });

        // Force remove popup and over styles when hovering map
        this.map.on('mousemove', event => {
            const features = this.getFeaturesAtPoint(event.point, allLayersIds);
            if (!features || features.length === 0) {
                layersIds.forEach(layerId => {
                    this.map.setFilter(`${layerId}-hover`, ['==', 'id', '']);
                });
                if (this.infoWindowManager) {
                    this.infoWindowManager.hideMarkerInfoWindow();
                }
            }
        });

        // Handle specific click on past sale/realtor/program marker
        this.map.on('click', event => {
            event.originalEvent.preventDefault();
            const features = this.getFeaturesAtPoint(event.point, allLayersIds);
            if (!features || features.length === 0) {
                return;
            }
            const feature = MapHelpers.getFirstPointFromFeatures(features);
            const layer = this.getFeatureLayer(feature.source);
            if (layer && typeof layer.clickHandler === 'function') {
                layer.clickHandler(event, feature, layer);
            }
        });
    }

    /**
     * Specific events for touch devices
     * @listens {MapboxGL.MapMouseEvent} listen dragend and click.
     * @listens {MouseEvent} listen click on [data-popin-button].
     */
    bindTouchEvents() {
        const layersIds = this._featureLayers.map(layer => layer.id);
        const layersOverIds = this._featureLayers.map(layer => `${layer.id}-hover`);
        const allLayersIds = layersOverIds.concat(layersIds);
        if (!isMobile() && this.infoWindowManager) {
            this.map.on('dragend', () => {
                this.infoWindowManager.hideMarkerInfoWindow();
            });
        }

        if (this.infoWindowManager) {
            this.map.on('click', event => {
                event.originalEvent.preventDefault();
                const features = this.getFeaturesAtPoint(event.point, allLayersIds);
                if (!features || features.length === 0) {
                    return;
                }

                const feature = MapHelpers.getFirstPointFromFeatures(features);
                const layer = this.getFeatureLayer(feature.source);

                if (layer && layer.infoWindowParameters) {
                    this.infoWindowManager.loadMarkerInfoWindow(
                        feature,
                        layer.infoWindowParameters,
                    );
                }
            });
        }
    }

    /**
     * When bounds changed (zoom or pan)
     */
    onBoundsChanged(callback) {
        if (typeof callback !== 'function') {
            return;
        }
        let debounceTimeout = 0;
        const onBoundsChanged = event => {
            if (deepEqual(this.oldBounds, this.map.getBounds())) {
                // Why ?? Method like map.resize() trigger this event
                return;
            }
            this.oldBounds = this.map.getBounds();
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(() => {
                callback(event);
            }, 200);
        };

        this.map.on('zoomend', onBoundsChanged);
        this.map.on('moveend', onBoundsChanged);
    }

    /**
     * Retrieve a registered layer by its ID
     * @param {String} layerId
     * @return {Object} Layer or undefined if not found
     */
    getFeatureLayer(layerId) {
        return this._featureLayers.find(layer => layerId === layer.id);
    }

    /**
     * Get features present under a given point
     * @param point
     * @param layers
     * @return {Array<Object>}
     */
    getFeaturesAtPoint(point, layers) {
        const width = MARKER_SIZE;
        const height = MARKER_SIZE;
        const features = this.map.queryRenderedFeatures(
            [
                [point.x - width / 2, point.y - height / 2],
                [point.x + width / 2, point.y + height / 2],
            ],
            {
                layers,
            },
        );
        return features;
    }

    /**
     * Change center of map.
     * @param {Object} coordinates New coordinates
     */
    setCenter(coordinates) {
        if (this.map) {
            this.map.setCenter(coordinates);
        } else {
            global.console.warning('No map loaded');
        }
    }

    /**
     * Bind events to Google Analytics events.
     * Assume the map is loaded
     * TODO Refactor to put this method out of class
     */
    setTracking() {
        const $wrapper = $(this.wrapperElement);
        const {
            uaEventCategory: eventCategory,
            uaEventLabelPrefix: eventLabelPrefix,
            waSource,
            waNamePrefix,
        } = $wrapper.data();
        const defaultGaValues = { hitType: 'event', eventCategory };

        $(window).on('gaSendFitBounds', event => {
            Ga.send({
                ...defaultGaValues,
                eventAction: `go:prices:${event.placeType}`,
                eventLabel: 'prices:map:click',
            });
        });

        if (!isMobile()) {
            this.map.on('click', `${REFERENCE_FEATURE_ID}-hover`, event => {
                const feature = MapHelpers.parseStringifiedFeatures(event.features[0]);
                const { id, realtor } = feature.properties;

                Ga.send({
                    ...defaultGaValues,
                    eventAction: 'go:past_sale',
                    eventLabel: `${eventLabelPrefix}:past_sales:marker`,
                });
                Wa.push(
                    'go:past_sale',
                    `${waNamePrefix}:past_sales`,
                    `source=${waSource}|realtor_id=${realtor.id}|reference_id=${id}`,
                );
            });

            this.map.on('click', `${REALTOR_FEATURE_ID}-hover`, event => {
                Ga.send({
                    ...defaultGaValues,
                    eventAction: 'go:realtor_page',
                    eventLabel: `${eventLabelPrefix}:realtors:marker`,
                });
                Wa.push(
                    'go:realtor',
                    `${waNamePrefix}:realtors`,
                    `source=${waSource}|realtor_id=${event.features[0].properties.id}`,
                );
            });
        }

        $wrapper.on(MAEvent.mapFeatureInfoWindowOpened, event => {
            const feature = MapHelpers.parseStringifiedFeatures(event.feature);
            const { source: featureType, properties } = feature;

            switch (featureType) {
                case OTHER_PAST_SALE_FEATURE_ID:
                case REFERENCE_FEATURE_ID:
                case RELEVANT_PAST_SALE_FEATURE_ID:
                    Ga.send({
                        ...defaultGaValues,
                        eventAction: `${$wrapper.data(
                            'ua-event-label-prefix',
                        )}:past_sales:infowindow:open`,
                        eventLabel: `${eventLabelPrefix}:past_sales:marker`,
                    });
                    Wa.push(
                        'print:past_sale',
                        `${waNamePrefix}:past_sales`,
                        `source=${waSource}|realtor_id=${properties.realtor.id}|reference_id=${properties.id}`,
                    );
                    break;
                case REALTOR_FEATURE_ID:
                    Ga.send({
                        ...defaultGaValues,
                        eventAction: `${$wrapper.data(
                            'ua-event-label-prefix',
                        )}:realtors:infowindow:open`,
                        eventLabel: `${eventLabelPrefix}:realtors:marker`,
                    });
                    Wa.push(
                        'print:realtor',
                        `${waNamePrefix}:realtors`,
                        `source=${waSource}|realtor_id=${properties.id}`,
                    );
                    break;
                case TRANSIT_STATION_FEATURE_ID:
                    setMediumParam();
                    Ga.send({
                        ...defaultGaValues,
                        eventAction: `${eventLabelPrefix}:transit_stations:infowindow:open`,
                        eventLabel: `${eventLabelPrefix}:transit_stations:marker`,
                    });
                    break;
                default:
                    break;
            }
        });

        $wrapper.on('click', '.mapboxgl-ctrl-zoom-in', () => {
            Ga.send({
                ...defaultGaValues,
                eventAction: `${eventLabelPrefix}:zoom_in`,
                eventLabel: `${eventLabelPrefix}:zoom_in_button`,
            });
        });

        $wrapper.on('click', '.mapboxgl-ctrl-zoom-out', () => {
            Ga.send({
                ...defaultGaValues,
                eventAction: `${eventLabelPrefix}:zoom_out`,
                eventLabel: `${eventLabelPrefix}:zoom_out_button`,
            });
        });
    }
}
